import {
  ArrowHelper,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  EdgesGeometry,
  Float32BufferAttribute,
  Intersection,
  Line3,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Plane,
  Quaternion,
  SphereGeometry,
  TypedArray,
  Vector3,
} from 'three';
import { HalfedgeDS, Vertex, Face, Halfedge } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { adjacenceBoundaries } from '@thirdPartyLibraries/three-mesh-halfedge/src/core/Vertex';
import { computeBoundsTree, HitPointInfo } from 'three-mesh-bvh';
import { Brush, CSGOperation, Evaluator } from 'three-bvh-csg';
import { RootState } from '@react-three/fiber/dist/declarations/src/core/store';
import aStar from '@modules/three/common/aStar';
import CameraControlsImpl from 'camera-controls';
import { DEG2RAD } from '@constants/MathGeometry';
import MeshAnchorCollection from '@modules/three/common/MeshAnchorCollection';
import RaycastEvents from '@modules/three/common/raycastEvents';
import { RaycastControl } from '@modules/three/common/raycastControl';
import { threeInteractionStore } from '@modules/three/store/threeInteractionStore';
import SortedArray from '@common/SortedArray';
import { ThreeRootState } from '@modules/three/common/BaseThreeScene';
import { threeStore } from '@modules/three/store/threeStore.ts';
import { PLYExporter } from 'three/examples/jsm/exporters/PLYExporter.js';
import { AdvancingFrontFiller } from './operations/AdvancingFrontFiller';
import { SmoothBorder, SmoothingOptions } from './operations/SmoothBorder';
import { PushVerticesTowardsPosition, PushTowardsPositionOptions } from './operations/PushVerticesTowardsPosition';
import { fillSides } from './operations/FillSides';
import { flattenBorderToY, FlattenBorderOptions, FlattenYMode } from './operations/FlattenBorderToY';
import { scaleVertices, ScaleVerticesOptions } from './operations/ScaleVertices';
import '@three/common/curve/curve-extensions';
import { EdgeIntersection } from '@/thirdPartyLibraries/three-mesh-halfedge/src/operations/cutEdges';
import {
  buildFaceSetOutlineGeometry,
  buildGeometryFromFaceSet,
  collectFacesNearMeshSurface,
  defaultProximityThresholdWorld as computeDefaultProximityThresholdWorld,
  faceCentroidWorld,
  type FaceProximityFilter,
  type FaceProximityHit,
  worldDistanceToMeshSurface,
} from './meshSurfaceProximity';
import { removeFaceSet as removeFaceSetOp, type RemoveFaceSetResult } from './operations/RemoveFaceSet';

BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;

const CHUNK_SIZE = 10000;

export interface PLYExportOptions {
  /** If true, writes little-endian binary; otherwise ASCII */
  binary?: boolean;
  /** Attributes to omit (e.g. ['normal', 'uv', 'color', 'index']) */
  excludeAttributes?: string[];
}

class InvalidNumberOfPointsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidNumberOfPointsError';
  }
}

export default class HalfEdgeMesh extends Mesh implements RaycastEvents, RaycastControl {
  public halfEdges = new HalfedgeDS();
  // Allow classes to opt out of participating in raycast selection
  public raycastEnabled = true;
  private AVERAGE_DISTANCE_LIMIT_MIN = -10;
  private AVERAGE_DISTANCE_LIMIT_MAX = -0.2;
  private readonly EDGES_THRESHOLD = 0.5;
  private VERTICES_COUNT_LIMIT = 100;
  protected borderVertices = new Array<Vertex>();
  protected rootState: RootState | ThreeRootState;
  protected movedPosition = new Vector3(0, 0, 0);
  protected geometryHelper = new LineSegments();
  protected boundaryVisualization?: LineSegments;
  protected cameraControls: CameraControlsImpl = null!;
  protected readonly anchorCollection: MeshAnchorCollection = new MeshAnchorCollection();
  protected isAnchorInsertionEnabled = false;
  public deleteAnchorsOnModeChange = true;
  private highlightObjects: Object3D[] = [];

  // Cache mapping from raw buffer indices to merged indices (renderIds)
  private vertexIndexArray = new Array<number>();

  /**
   * Faces this app GENERATED rather than scanned — the display-only
   * interproximal walls and gum bridges segmentation adds so the crowns look
   * closed. The correction export deletes them again and rebuilds the
   * interdental space between the posed crowns, and all it gets to go on is
   * one integer per model (see `getGeneratedFacesFrom`), so the marks have to
   * survive every edit the segmentation still makes after them.
   *
   * They are held as face IDENTITIES, not indices, because the DS face pool
   * swap-pops on removal (see TrackedArray): one edge collapse anywhere moves
   * the last face into the freed slot, and any index recorded earlier then
   * points at the wrong triangle. Identity is immune to that — and
   * `toBufferGeometry` writes the marked faces last, so the exported buffer
   * (and the PLY made from it) has them as a contiguous suffix no matter what
   * order the pool ended up in.
   */
  public readonly generatedFaces = new Set<Face>();

  constructor(geometry: BufferGeometry, addToScene = true) {
    super();
    this.rootState = threeStore.getState().rootState;
    this.cameraControls = threeStore.getState().cameraControls!;
    try {
      this.setNewGeometry(geometry);
    } catch (error) {
      console.error('Error setting new geometry', error);
    }
    this.material = new MeshBasicMaterial({ vertexColors: true, side: DoubleSide });
    // Default to casting and receiving shadows; specific materials can still override visuals
    this.castShadow = true;
    this.receiveShadow = true;
    if (addToScene) {
      this.rootState.scene.add(this);
    }
    threeInteractionStore.subscribe((state) => {
      this.setAnchorInsertion(state.pointAdding);
    });
  }

  public setRaycastEnabled(enabled: boolean) {
    this.raycastEnabled = !!enabled;
  }

  public isRaycastEnabled(): boolean {
    return this.raycastEnabled;
  }

  public raycastHit(intersection: Intersection<Object3D>) {
    if (!this.isAnchorInsertionEnabled) {
      console.warn('Anchor insertion is not enabled');
      return;
    }
    if (!intersection.face) {
      throw new Error('No face detected'); //TODO handle this error
    }
    const cameraWorldPosition = new Vector3();
    this.rootState.camera.getWorldPosition(cameraWorldPosition);
    const intersectVertex = this.halfEdges.renderIdToVertex.get(intersection.face.a);
    this.anchorCollection.addAnchor(intersection.point, intersectVertex!, cameraWorldPosition);
  }

  public setNewGeometry(geometry: BufferGeometry) {
    // Rebuilding the DS mints new Face objects, so the generated-face marks —
    // which are identities — do not survive it. Re-anchor them by the split
    // instead: toBufferGeometry always writes the generated faces last, so in
    // a geometry that came from this mesh they are still the tail. Anything
    // else (a foreign geometry replacing the mesh outright) invalidates them,
    // and it is better to lose the marks loudly than to delete scan triangles
    // in the export.
    const generatedFrom = this.getGeneratedFacesFrom();
    const facesBefore = generatedFrom >= 0 ? this.countTriangularFaces() : -1;
    this.generatedFaces.clear();

    this.vertexIndexArray = this.halfEdges.setFromGeometry(geometry);
    if (generatedFrom >= 0) {
      if (this.halfEdges.faces.length === facesBefore) {
        this.markGeneratedFacesFrom(generatedFrom);
      } else {
        console.warn(
          `setNewGeometry: dropped ${facesBefore - generatedFrom} generated-face mark(s) — ` +
            `the new geometry has ${this.halfEdges.faces.length} faces, not ${facesBefore}`,
        );
      }
    }
    this.geometry = geometry;
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundsTree();
    this.geometry.computeBoundingBox();
    this.calculateBorderVertices();
    this.geometry.setAttribute('uv', new BufferAttribute(new Float32Array([]), 1));
    const posAttr = this.geometry.getAttribute('position') as BufferAttribute;
    const existingColor = this.geometry.getAttribute('color') as BufferAttribute | undefined;
    if (!existingColor || existingColor.count !== posAttr.count) {
      const colors = new Float32Array(posAttr.count * 3);
      for (let i = 0; i < posAttr.count; i++) {
        colors[i * 3 + 0] = 1.0;
        colors[i * 3 + 1] = 1.0;
        colors[i * 3 + 2] = 1.0;
      }
      this.geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    }
  }

  private getFaceMergedIndexTriple(faceIndex: number): [number, number, number] {
    const arr = this.geometry.getIndex()!.array as ArrayLike<number>;
    const base = faceIndex * 3;
    return [
      this.vertexIndexArray[arr[base + 0]],
      this.vertexIndexArray[arr[base + 1]],
      this.vertexIndexArray[arr[base + 2]],
    ];
  }

  public calculateBorderVertices() {
    this.borderVertices = [];
    this.halfEdges.vertices.forEach((vertex) => {
      if (vertex.isFree()) {
        this.borderVertices.push(vertex);
      }
    });
  }

  protected async expandVerticesAsync(vertices: Vertex[]): Promise<Vertex[]> {
    const out: Vertex[] = [];

    for (let i = 0; i < vertices.length; i++) {
      const nbrs = this.getAdjacenceVertices(vertices[i], 8);
      out.push(...nbrs);
      await this.yieldIfNeeded(i);
    }

    return out;
  }

  /**
   * Compute the edges of the mesh which have an angle between the normal of the two adjacent faces
   * greater than the given threshold angle.
   * @param {number} thresholdAngle The threshold angle in degrees.
   * @returns {Set<number>} A set of vertex indices which are part of the edges with an angle greater than the threshold angle.
   */
  public async computeEdgesWithThresholdAsync(thresholdAngle?: number): Promise<Set<Vertex>> {
    if (thresholdAngle === undefined) {
      thresholdAngle = this.EDGES_THRESHOLD;
    }
    const featureEdges = new Set<Vertex>();
    const cosThreshold = Math.cos(thresholdAngle * DEG2RAD);
    const halfedges = this.halfEdges.halfedges;

    for (let i = 0; i < halfedges.length; i++) {
      const he = halfedges[i];
      const n1 = new Vector3(),
        n2 = new Vector3();
      he.face?.getNormal(n1);
      he.twin.face?.getNormal(n2);
      if (n1.dot(n2) < cosThreshold) {
        featureEdges.add(he.vertex);
        featureEdges.add(he.twin.vertex);
      }
      await this.yieldIfNeeded(i);
    }

    return featureEdges;
  }

  /**
   * Synchronous and more robust feature-edge detection.
   * - Processes each undirected edge once (deduplicated by vertex ids).
   * - Skips boundary edges to reduce noise.
   * - Uses area-weighted face normals with optional neighbor smoothing.
   */
  public computeEdgesWithThreshold(thresholdAngle?: number, smoothNeighbors = 1): Set<Vertex> {
    if (thresholdAngle === undefined) thresholdAngle = this.EDGES_THRESHOLD;
    const cosThreshold = Math.cos(thresholdAngle * DEG2RAD);
    const ds = this.halfEdges;
    const out = new Set<Vertex>();

    // Precompute per-face area and unit normal
    const faceArea = new Map<Face, number>();
    const faceNormal = new Map<Face, Vector3>();
    const tmp = new Vector3();
    const tmp2 = new Vector3();
    for (const f of ds.faces) {
      const a = f.halfedge.prev.vertex.position;
      const b = f.halfedge.vertex.position;
      const c = f.halfedge.next.vertex.position;
      tmp.copy(b).sub(a);
      tmp2.copy(c).sub(a);
      const n = new Vector3().crossVectors(tmp, tmp2);
      const area = Math.max(1e-12, 0.5 * n.length());
      if (n.lengthSq() > 1e-20) n.normalize();
      faceArea.set(f, area);
      faceNormal.set(f, n);
    }

    const smoothNormal = (f: Face): Vector3 => {
      if (smoothNeighbors <= 0) return faceNormal.get(f)!.clone();
      const visited = new Set<Face>();
      const queue: Array<{ face: Face; d: number }> = [{ face: f, d: 0 }];
      const acc = new Vector3();
      let wsum = 0;
      while (queue.length) {
        const { face, d } = queue.shift()!;
        if (visited.has(face)) continue;
        visited.add(face);
        const n = faceNormal.get(face);
        const a = faceArea.get(face) ?? 1;
        if (n) {
          const w = 1 / (1 + d);
          acc.addScaledVector(n, a * w);
          wsum += a * w;
        }
        if (d < smoothNeighbors) {
          let he = face.halfedge;
          for (let k = 0; k < 3; k++) {
            const nf = he.twin.face;
            if (nf) queue.push({ face: nf, d: d + 1 });
            he = he.next;
          }
        }
      }
      if (wsum > 0) acc.multiplyScalar(1 / wsum);
      if (acc.lengthSq() > 1e-20) acc.normalize();
      return acc;
    };

    // Iterate unique edges
    const seen = new Set<string>();
    for (const he of ds.halfedges) {
      const a = he.vertex.id;
      const b = he.twin.vertex.id;
      const key = a < b ? `${a}-${b}` : `${b}-${a}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const fL = he.face;
      const fR = he.twin.face;
      if (!fL || !fR) continue; // skip boundary

      const nL = smoothNormal(fL);
      const nR = smoothNormal(fR);
      const cos = nL.dot(nR);
      if (!isFinite(cos)) continue;
      if (cos < cosThreshold) {
        out.add(he.vertex);
        out.add(he.twin.vertex);
      }
    }

    return out;
  }

  /**
   * Calculate the set of convex vertices of the mesh.
   *
   * This method classifies each vertex as convex or non-convex by computing the average signed distance
   * of its neighbors to the plane defined by the vertex normal and position. The average signed distance
   * is a measure of how much the neighbors of a vertex are on the same side of the plane defined by the
   * vertex normal and position. If the average signed distance is negative, the vertex is classified as
   * convex.
   *
   * The method returns a set of vertex indices that are classified as convex.
   *
   * @returns The set of convex vertices.
   */
  public async calculateConvexVerticesAsync(): Promise<Set<Vertex>> {
    const convex = new Set<Vertex>();
    const verts = this.halfEdges.vertices;

    for (let i = 0; i < verts.length; i++) {
      const v = verts[i];
      const pos = v.position;
      const normal = new Vector3();
      v.halfedge?.face?.getNormal(normal);
      const plane = new Plane().setFromNormalAndCoplanarPoint(normal, pos);

      let avgDist = 0;
      const neighbors = this.getAdjacenceVertices(v, this.VERTICES_COUNT_LIMIT);
      for (let j = 0; j < neighbors.length; j++) {
        avgDist += plane.distanceToPoint(neighbors[j].position);
      }

      if (avgDist < 0 && avgDist > this.AVERAGE_DISTANCE_LIMIT_MIN && avgDist < this.AVERAGE_DISTANCE_LIMIT_MAX) {
        convex.add(v);
      }
      await this.yieldIfNeeded(i);
    }

    return convex;
  }

  public getAdjacenceVertices(startingVertex: Vertex, verticesCount: number): Array<Vertex> {
    const startingHalfEdge = startingVertex.halfedge;
    if (startingHalfEdge === null) {
      return [];
    }
    const startingHalfEdges = [startingHalfEdge, startingHalfEdge.next, startingHalfEdge.prev];
    if (startingHalfEdges.length !== 3) {
      return [];
    }
    const [startingHalfEdgeA, startingHalfEdgeB, startingHalfEdgeC] = startingHalfEdges;
    const queue = [startingHalfEdgeA, startingHalfEdgeB, startingHalfEdgeC];
    const visited = new Set<string>();
    const result = [startingHalfEdgeA.vertex, startingHalfEdgeB.vertex, startingHalfEdgeC.vertex];

    while (queue.length > 0 && result.length < verticesCount) {
      const actualHalfEdge = queue.shift()!;
      if (visited.has(actualHalfEdge.id)) {
        continue;
      }

      const halfEdgeTwin = actualHalfEdge.twin;
      visited.add(actualHalfEdge.id);
      visited.add(halfEdgeTwin.id);
      result.push(actualHalfEdge.vertex);

      if (!visited.has(halfEdgeTwin.next.id)) {
        queue.push(halfEdgeTwin.next);
      }
      if (!visited.has(halfEdgeTwin.prev.id)) {
        queue.push(halfEdgeTwin.prev);
      }
    }
    return result;
  }

  protected solidModelling(geometry2: BufferGeometry, operation: CSGOperation): BufferGeometry {
    if (geometry2.getAttribute('normal') === undefined) {
      geometry2.computeVertexNormals();
    }
    if (geometry2.boundsTree === undefined) {
      geometry2.computeBoundsTree();
    }

    const brush1 = new Brush(this.geometry);
    brush1.material = new MeshBasicMaterial({ color: 0xff0000, side: DoubleSide });
    const brush2 = new Brush(geometry2);
    brush2.material = new MeshBasicMaterial({ color: 0x0000ff, side: DoubleSide });
    brush1.geometry.setAttribute('uv', new Float32BufferAttribute([], 1));
    brush2.geometry.setAttribute('uv', new Float32BufferAttribute([], 1));

    const evaluator = new Evaluator();
    const result = evaluator.evaluate(brush1, brush2, operation);
    result.prepareGeometry();
    return result.geometry;
  }

  public SolidModellingOperation(geometry1: BufferGeometry, geometry2: BufferGeometry, operation: CSGOperation): BufferGeometry {
    if (geometry1.getAttribute('normal') === undefined) {
      geometry1.computeVertexNormals();
    }
    if (geometry1.boundsTree === undefined) {
      geometry1.computeBoundsTree();
    }
    if (geometry2.getAttribute('normal') === undefined) {
      geometry2.computeVertexNormals();
    }
    if (geometry2.boundsTree === undefined) {
      geometry2.computeBoundsTree();
    }

    const brush1 = new Brush(geometry1);
    brush1.material = new MeshBasicMaterial({ color: 0xff0000, side: DoubleSide });
    const brush2 = new Brush(geometry2);
    brush2.material = new MeshBasicMaterial({ color: 0x0000ff, side: DoubleSide });
    brush1.geometry.setAttribute('uv', new Float32BufferAttribute([], 1));
    brush2.geometry.setAttribute('uv', new Float32BufferAttribute([], 1));

    const evaluator = new Evaluator();
    const result = evaluator.evaluate(brush1, brush2, operation);
    result.prepareGeometry();
    return result.geometry;
  }

  public getBorderVertices(): Array<Vertex> {
    return this.borderVertices;
  }

  /**
   * Computes intersection points between this mesh's surface and another mesh using BVH acceleration.
   *
   * This method performs a BVH cast of `mesh2` against `this` mesh, but only processes intersections
   * that occur on triangles belonging to `this` mesh. For each intersected triangle, it:
   *   1. Retrieves the triangle's vertex indices from the BufferGeometry index buffer.
   *   2. Uses the VerticesToFaceMap to map those vertex IDs to the corresponding Face in the half-edge mesh.
   *   3. Computes the barycentric coordinates of the intersection points relative to the triangle vertices.
   *   4. Determines which edge of the triangle the point lies on (within a small epsilon tolerance).
   *   5. Locates the corresponding HalfEdge in the Face.
   *
   * Returns an array of objects containing:
   *   - `point`: Intersection point in local coordinates of this mesh.
   *   - `halfEdge`: The HalfEdge in the half-edge mesh that corresponds to the intersected edge.
   *
   * @param mesh2 - The other mesh to test against. Its geometry must have a BVH (`boundsTree`) built.
   * @returns Array of intersection records containing the point and corresponding HalfEdge in this mesh.
   * @throws Error if BVH trees are missing from either mesh geometry.
   */

  public intersectMesh(mesh2: Mesh): EdgeIntersection[] {
    if (!this.geometry.boundsTree || !mesh2.geometry.boundsTree) {
      throw new Error('BoundsTree not found for mesh1 or mesh2');
    }

    // Transform mesh2 do lokálního prostoru mesh1
    const M2to1 = new Matrix4().copy(this.matrixWorld).invert().multiply(mesh2.matrixWorld);

    const results: EdgeIntersection[] = [];
    const edge = new Line3();

    this.geometry.boundsTree.bvhcast(mesh2.geometry.boundsTree, M2to1, {
      intersectsTriangles: (triangle1, triangle2, i1) => {
        if (triangle1.intersectsTriangle(triangle2, edge)) {
          const { start, end } = edge;

          // Get merged vertex indices for this triangle
          const [a1, b1, c1] = this.getFaceMergedIndexTriple(i1);

          // Find Face in half-edge mesh by vertex ID
          const va = this.halfEdges.renderIdToVertex.get(a1);
          const vb = this.halfEdges.renderIdToVertex.get(b1);
          const vc = this.halfEdges.renderIdToVertex.get(c1);
          if (!va || !vb || !vc) return false;
          const face = this.halfEdges.verticesToFace.get(va.id, vb.id, vc.id);
          if (!face) return false;

          // Find HalfEdge for each intersection
          const heStart = face.halfedgeFromPosition(start);
          const heEnd = face.halfedgeFromPosition(end);

          if (heStart) results.push({ point: start.clone(), halfEdge: heStart });
          if (heEnd) results.push({ point: end.clone(), halfEdge: heEnd });
        }
        return false; // continue traversal
      },
    });

    return results;
  }

  /**
   * Like {@link intersectMesh}, but returns one segment per intersected triangle (cut line on this mesh).
   */
  public intersectMeshSegments(mesh2: Mesh): Array<{ a: Vector3; b: Vector3 }> {
    if (!this.geometry.boundsTree || !mesh2.geometry.boundsTree) {
      throw new Error('BoundsTree not found for mesh1 or mesh2');
    }

    const M2to1 = new Matrix4().copy(this.matrixWorld).invert().multiply(mesh2.matrixWorld);
    const segments: Array<{ a: Vector3; b: Vector3 }> = [];
    const edge = new Line3();

    this.geometry.boundsTree.bvhcast(mesh2.geometry.boundsTree, M2to1, {
      intersectsTriangles: (triangle1, triangle2) => {
        if (triangle1.intersectsTriangle(triangle2, edge)) {
          const { start, end } = edge;
          if (start.distanceToSquared(end) > 1e-12) {
            segments.push({ a: start.clone(), b: end.clone() });
          }
        }
        return false;
      },
    });

    return segments;
  }

  /**
   * Centers the geometry of the mesh at the origin and adjusts the mesh's position
   * to maintain its original relative position in the scene.
   *
   * This function calculates the difference between the old and new positions of
   * the first vertex, then applies this difference to the mesh's position to
   * ensure it remains in the same location relative to other objects.
   */
  protected centerOrigin() {
    const oldPositions = this.geometry.attributes.position.array;
    const oldVertex = new Vector3(oldPositions[0], oldPositions[1], oldPositions[2]);
    this.geometry.center();
    const newPositions = this.geometry.attributes.position.array;
    const newVertex = new Vector3(newPositions[0], newPositions[1], newPositions[2]);
    this.position.copy(oldVertex.sub(newVertex));
    this.syncHalfEdgePositionsFromGeometry();
  }

  /**
   * Copies render buffer positions into half-edge vertex.position.
   * Required after geometry-only edits (e.g. centerOrigin) so topology coords match the mesh.
   */
  public syncHalfEdgePositionsFromGeometry() {
    const positions = this.geometry.getAttribute('position') as BufferAttribute;
    for (const vertex of this.halfEdges.vertices) {
      const renderId = this.halfEdges.vertexToRenderId.get(vertex);
      if (renderId === undefined) continue;
      vertex.position.set(positions.getX(renderId), positions.getY(renderId), positions.getZ(renderId));
    }
  }

  /** World position of a vertex as rendered (buffer local pos × mesh world matrix). */
  public getVertexWorldPosition(vertex: Vertex, target = new Vector3()): Vector3 {
    const positions = this.geometry.getAttribute('position') as BufferAttribute;
    const renderId = this.halfEdges.vertexToRenderId.get(vertex);
    if (renderId === undefined) {
      return target.copy(vertex.position);
    }
    target.set(positions.getX(renderId), positions.getY(renderId), positions.getZ(renderId));
    return this.localToWorld(target);
  }

  /**
   * Colors all vertices in the mesh with the given color.
   *
   * @param color The color to set all vertices to.
   */
  protected colorAllVertices(color: Color) {
    const colors = new Float32Array(this.geometry.attributes.position.count * 3);
    colors.fill(color.r, 0, colors.length);
    this.geometry.setAttribute('color', new BufferAttribute(colors, 3));
  }

  /**
   * Colors the specified vertices in the mesh with the given color.
   *
   * @param color The color to set the specified vertices to.
   * @param vertices The indices of the vertices to color.
   */
  public colorVertices(color: Color, vertices: number[] | Array<Vertex>) {
    if (typeof vertices[0] !== 'number') {
      vertices = vertices.map((v) => this.halfEdges.vertexToRenderId.get(v as Vertex)!);
    }
    vertices = vertices as number[];

      const count = this.geometry.attributes.position.count;
  
      let colorAttr = this.geometry.getAttribute('color') as BufferAttribute | null;
      // Ensure a color attribute exists with the right size
      if (!colorAttr || colorAttr.count !== count) {
        const newColors = new Float32Array(count * 3);
  
        // Try to copy existing colors if present (best-effort)
        if (colorAttr?.array) {
          const old = colorAttr.array as ArrayLike<number>;
          const len = Math.min(old.length, newColors.length);
          for (let i = 0; i < len; i++) newColors[i] = Number(old[i]);
        }
  
        colorAttr = new Float32BufferAttribute(newColors, 3);
        this.geometry.setAttribute('color', colorAttr);
      }
  
      const colors = colorAttr.array as Float32Array;
  
      // Update only provided vertex indices
      for (let i = 0; i < vertices.length; i++) {
        const vi = vertices[i];
        const idx = vi * 3;
        if (idx < 0 || idx + 2 >= colors.length) continue; // safety
        colors[idx + 0] = color.r;
        colors[idx + 1] = color.g;
        colors[idx + 2] = color.b;
      }
  
      colorAttr.needsUpdate = true;
    }

  /**
   * Given a point in 3D space, returns the vertex of the mesh that is closest to that point.
   * @param point The point in 3D space to search for the closest vertex.
   * @returns The closest vertex to the given point.
   */
  public getClosestVertexToPoint(point: Vector3): Vertex | null {
    const hitPointInfo: HitPointInfo = {} as HitPointInfo;
    this.geometry.boundsTree?.closestPointToPoint(point, hitPointInfo);
    const faceIdx = hitPointInfo.faceIndex;
    if (faceIdx === undefined) return null;
    
    const [ia, ib, ic] = this.getFaceMergedIndexTriple(faceIdx);
    const va = this.halfEdges.renderIdToVertex.get(ia);
    const vb = this.halfEdges.renderIdToVertex.get(ib);
    const vc = this.halfEdges.renderIdToVertex.get(ic);
    
    // Safety: after mesh modifications, some vertices may no longer exist
    if (!va && !vb && !vc) return null;
    
    const target = hitPointInfo.point ?? point;
    const candidates: Array<{ v: Vertex; d: number }> = [];
    if (va) candidates.push({ v: va, d: va.position.distanceToSquared(target) });
    if (vb) candidates.push({ v: vb, d: vb.position.distanceToSquared(target) });
    if (vc) candidates.push({ v: vc, d: vc.position.distanceToSquared(target) });
    
    candidates.sort((a, b) => a.d - b.d);
    return candidates[0]?.v ?? null;
  }

  /**
   * Creates a helper mesh that shows the edges of the mesh' geometry and adds it
   * to the scene. This is useful for debugging purposes. The helper mesh is
   * removed before adding the new one, so only one helper mesh is added to the
   * scene at any time.
   */
  public addGeometryHelper() {
    this.removeGeometryHelper();

    // Create wireframe for edges
    const edges = new EdgesGeometry(this.geometry);
    const lineMaterial = new LineBasicMaterial({ color: 0x0000ff });
    this.geometryHelper = new LineSegments(edges, lineMaterial);
    this.rootState.scene.add(this.geometryHelper);

    // Create a semi-transparent mesh to show faces
    const faceMaterial = new MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.3,
      side: DoubleSide,
    });
    const faceMesh = new Mesh(this.geometry, faceMaterial);
    faceMesh.name = 'face-helper';
    this.rootState.scene.add(faceMesh);
  }

  protected removeGeometryHelper() {
    this.rootState.scene.remove(this.geometryHelper);

    // Also remove the face helper mesh
    const faceHelper = this.rootState.scene.getObjectByName('face-helper');
    if (faceHelper) {
      this.rootState.scene.remove(faceHelper);
    }
  }

  public getPositions(): TypedArray {
    return this.geometry.attributes.position.array;
  }

  public getIndices(): TypedArray {
    return this.geometry.index!.array;
  }

  public getColors(): TypedArray {
    return this.geometry.attributes.color.array;
  }

  /**
   * Move a vertex to a new position in the mesh. The vertex' position, and the
   * corresponding vertex in the mesh' geometry are updated.
   *
   * @param vertex The vertex to move.
   * @param position The new position of the vertex.
   */
  public moveVertex(vertex: Vertex, position: Vector3) {
    vertex.position.copy(position);
    this.geometry.attributes.position.setXYZ(this.halfEdges.vertexToRenderId.get(vertex)!, position.x, position.y, position.z);
  }

  /**
   * Move all halfedges by the given distance. The position of the vertices
   * and the corresponding vertex in the mesh' geometry are updated.
   *
   * @param distance The distance to move the vertices.
   */
  public moveHalfEdges(distance: Vector3) {
    this.halfEdges.vertices.forEach((vertex) => {
      vertex.position.add(distance);
    });
    this.movedPosition = distance;
  }

  /**
   * Rotates all halfedges by the given quaternion around the given position.
   * @param quaternion The quaternion to rotate by.
   * @param position The position to rotate around.
   */
  public rotateHalfEdges(quaternion: Quaternion, position: Vector3) {
    const positions = this.geometry.getAttribute('position').array as ArrayLike<number>;
    this.halfEdges.vertices.forEach((vertex) => {
      const newPosition = new Vector3(
        positions[this.halfEdges.vertexToRenderId.get(vertex)! * 3],
        positions[this.halfEdges.vertexToRenderId.get(vertex)! * 3 + 1],
        positions[this.halfEdges.vertexToRenderId.get(vertex)! * 3 + 2],
      );
      newPosition.applyQuaternion(quaternion).add(position);
      vertex.position.copy(newPosition);
    });
  }

  public setPosition(position: Vector3) {
    const movedPosition = position.clone().sub(this.position);
    this.position.copy(position);
    this.moveHalfEdges(movedPosition);
  }

  public bfsUntilStops(start: Vertex, stopSet: Set<Vertex>): Set<Vertex> {
    const visited = new Set<Vertex>();
    const queue: Vertex[] = [];

    visited.add(start);
    queue.push(start);

    while (queue.length > 0) {
      const current = queue.shift()!;

      // If current is one of the stop vertices, do not expand from here.
      if (stopSet.has(current)) continue;

      const neighbors = current.adjacentVertices();
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    return visited;
  }

  /*
  public getLowestVertex(): Vertex {
    let lowest = this.halfEdges.vertices[0];
    for (const vertex of this.halfEdges.vertices) {
      if (vertex.position.y < lowest.position.y) {
        lowest = vertex;
      }
    }
    return lowest;
  }
  */

  /**
   * Finds the closest paths of vertices between the given vertices in the mesh.
   * The method returns a set of vertices that are part of the closest paths.
   * @param vertices the vertices in the mesh
   * @returns a set of vertices that are part of the closest paths
   */
  public findClosestPaths(vertices: Array<Vertex>): Set<Vertex> {
    const path = new Array<Vertex>();
    for (let i = 0; i < vertices.length - 1; i++) {
      path.push(...aStar(vertices[i], vertices[i + 1])!);
    }
    path.push(...aStar(vertices[vertices.length - 1], vertices[0])!);
    return new Set(path);
  }

  /**
   * Finds the closest vertices in the jaw mesh to the given concave hull points.
   * The concave hull points are 2D points that are the result of a concave hull algorithm.
   * The method returns an array of vertices that are the closest to the concave hull points.
   * @param teethConcaveHull the concave hull points in 2D coordinates
   * @param targetVertices the vertices in the jaw mesh
   * @returns an array of vertices that are the closest to the concave hull points
   */
  public findClosestVerticesToConcaveHull(teethConcaveHull: Array<Array<number>>, targetVertices: Vertex[]): Array<Vertex> {
    return teethConcaveHull.map((points) => {
      const closestVertex = targetVertices.reduce(
        (closest, vertex) => {
          const distance = Math.hypot(points[0] - vertex.position.x, points[1] - vertex.position.z);
          if (distance < closest.distance) {
            return { vertex, distance };
          }
          return closest;
        },
        { vertex: null as Vertex | null, distance: Infinity },
      );
      return closestVertex.vertex!;
    });
  }

  /**
   * Creates a shell geometry by offsetting the vertices of the current mesh.
   * The shell consists of an outer and an inner layer with faces connecting them.
   *
   * @param offset The distance by which to offset the inner layer of the shell.
   * @returns A BufferGeometry representing the shell with offset inner vertices.
   */
  public createShellGeometry(offset: number): BufferGeometry {
    const vertexCount = this.halfEdges.vertices.length;
    const newPositions: number[] = [];
    const newNormals: number[] = [];

    const vertexToBufferIndex = new Map<Vertex, number>();

    // (1) Outer vertices
    for (let i = 0; i < vertexCount; i++) {
      const vertex = this.halfEdges.vertices[i];
      const pos = vertex.position;
      const norm = vertex.computeVertexNormal();
      newPositions.push(pos.x, pos.y, pos.z);
      newNormals.push(norm.x, norm.y, norm.z);
      vertexToBufferIndex.set(vertex, i);
    }

    // (2) Inner vertices
    for (let i = 0; i < vertexCount; i++) {
      const vertex = this.halfEdges.vertices[i];
      const pos = vertex.position.clone();
      const norm = new Vector3();
      for (const adjacenceVertex of vertex.findNAdjacentVertices(100)) {
        if (adjacenceVertex === null) continue;
        norm.add(adjacenceVertex.computeVertexNormal());
      }
      norm.normalize();
      pos.addScaledVector(norm, offset);
      newPositions.push(pos.x, pos.y, pos.z);
      newNormals.push(norm.x, norm.y, norm.z);
    }

    const newIndices: number[] = [];

    // Outer and inner faces.
    for (let f = 0; f < this.halfEdges.faces.length; f++) {
      const face = this.halfEdges.faces[f].getVertices();
      const a = vertexToBufferIndex.get(face[0])!;
      const b = vertexToBufferIndex.get(face[1])!;
      const c = vertexToBufferIndex.get(face[2])!;
      newIndices.push(a, b, c);
      newIndices.push(c + vertexCount, b + vertexCount, a + vertexCount);
    }

    const orderedBoundary = this.extractOrderedBoundaryLoop();
    if (orderedBoundary.length > 0) {
      for (let i = 0; i < orderedBoundary.length; i++) {
        const va = orderedBoundary[i];
        const vb = orderedBoundary[(i + 1) % orderedBoundary.length];
        const a = vertexToBufferIndex.get(va)!;
        const b = vertexToBufferIndex.get(vb)!;
        newIndices.push(a, b, b + vertexCount);
        newIndices.push(a, b + vertexCount, a + vertexCount);
      }
    }

    const shellGeometry = new BufferGeometry();
    shellGeometry.setAttribute('position', new BufferAttribute(new Float32Array(newPositions), 3));
    shellGeometry.setAttribute('normal', new BufferAttribute(new Float32Array(newNormals), 3));
    shellGeometry.setIndex(new BufferAttribute(new Uint32Array(newIndices), 1));

    return shellGeometry;
  }

  /**
   * Returns the ordered boundary loop of the mesh.
   * The boundary loop is an array of vertices in the order they appear along the boundary of the mesh.
   * The loop is traversed in the direction of the boundary halfedges.
   * If the mesh has no boundary, an empty array is returned.
   * @returns the ordered boundary loop
   */
  private extractOrderedBoundaryLoop(): Vertex[] {
    if (this.borderVertices.length === 0) return [];

    const startVertex = this.borderVertices[0];
    const orderedVertices: Vertex[] = [];
    let currentVertex = startVertex;

    do {
      orderedVertices.push(currentVertex);
      // Get the next boundary halfedge from currentVertex.
      let nextVertex: Vertex | null = null;
      for (const he of currentVertex.boundaryHalfedgesOutLoop()) {
        // The next vertex is the destination of the halfedge.
        nextVertex = he.next.vertex;
        break; // take the first one
      }
      if (!nextVertex || nextVertex === startVertex) break;
      currentVertex = nextVertex;
    } while (currentVertex !== startVertex);

    return orderedVertices;
  }

  /**
   * Repairs vertices where normals rapidly change.
   * @param normalThreshold The normalThreshold for normal difference to trigger vertex repair.
   * @param positionThreshold The positionThreshold for position difference to trigger vertex repair.
   */
  public repairVertices(normalThreshold: number, positionThreshold: number) {
    for (const vertex of this.halfEdges.vertices) {
      const adjacentVertices = Array.from(vertex.findNAdjacentVertices(6));
      if (adjacentVertices.length === 0) continue;

      const meanNormal = new Vector3();
      const meanPosition = new Vector3();
      for (const adjVertex of adjacentVertices) {
        meanNormal.add(adjVertex.computeVertexNormal());
        meanPosition.add(adjVertex.position);
      }
      meanNormal.divideScalar(adjacentVertices.length);
      meanPosition.divideScalar(adjacentVertices.length);

      const vertexNormal = vertex.computeVertexNormal();
      const positionDifference = vertex.position.distanceTo(meanPosition);

      if (vertexNormal.angleTo(meanNormal) > normalThreshold || positionDifference > positionThreshold) {
        vertex.position.copy(meanPosition);
      }
    }
  }

  /**
   * Fixes the rotation of the mesh so that its base is parallel to the XY plane.
   * @throws InvalidNumberOfPointsError if the number of points is not 3
   */
  public fixRotation(): Quaternion {
    if (this.anchorCollection.length !== 3) {
      throw new InvalidNumberOfPointsError('Invalid number of points for rotation');
    }
    const [a, b, c] = this.anchorCollection;
    const plane = new Plane().setFromCoplanarPoints(a.position, b.position, c.position);
    const targetNormal = new Vector3(0, -1, 0); // Normal of the XY plane
    const quaternion = new Quaternion().setFromUnitVectors(plane.normal.normalize(), targetNormal);
    this.setRotationFromQuaternion(quaternion);
    return quaternion;
  }

  public setRotationFromQuaternion(quaternion: Quaternion) {
    this.halfEdges.vertices.forEach((vertex) => {
      const position = vertex.position.clone();
      position.applyQuaternion(quaternion);
      vertex.position.copy(position);
    });
    super.setRotationFromQuaternion(quaternion);
    this.updateMatrix();
  }

  public setAnchorInsertion(value: boolean) {
    if (value) {
      this.isAnchorInsertionEnabled = true;
    } else {
      if (this.deleteAnchorsOnModeChange) {
        this.anchorCollection.removeAll();
      }
      this.isAnchorInsertionEnabled = false;
    }
  }

  public getMeshAnchorCollection(): MeshAnchorCollection {
    return this.anchorCollection;
  }

  public async calculateEdges(): Promise<number[]> {
    const edgesSet = await this.computeEdgesWithThresholdAsync(this.EDGES_THRESHOLD);
    const convex = await this.calculateConvexVerticesAsync();
    const filtered = [...edgesSet].filter((v) => !convex.has(v));

    const expanded = await this.expandVerticesAsync(filtered);
    return Array.from(new Set(expanded.map((v) => v.renderId)));
  }

  /**
   * Export this mesh (with all its current transforms baked into the vertices)
   * to PLY and return a Blob.
   */
  public toPly(options: PLYExportOptions = {}): Promise<Blob> {
    return new Promise((resolve) => {
      this.updateMatrixWorld(true);

      const baked = this.geometry.clone();
      baked.applyMatrix4(this.matrixWorld);

      const tempMesh = new Mesh(baked);

      const exporter = new PLYExporter();
      exporter.parse(
        tempMesh,
        (data: string | ArrayBuffer) => {
          const blob = new Blob([data], {
            type: options.binary ? 'application/octet-stream' : 'text/plain',
          });
          resolve(blob);
        },
        {
          binary: options.binary ?? true,
          excludeAttributes: options.excludeAttributes ?? [],
        },
      );
    });
  }

  private async yieldIfNeeded(i: number): Promise<void> {
    if (i % CHUNK_SIZE === 0) {
      await new Promise((resolve) => setTimeout(resolve, 1));
    }
  }

  /**
   * Records every face from pool index `from` onwards as generated (see
   * {@link generatedFaces}). Call it right after an operation that only ADDS
   * faces, with the pool length captured just before it.
   *
   * @returns how many faces were marked
   */
  public markGeneratedFacesFrom(from: number): number {
    const faces = this.halfEdges.faces;
    for (let f = Math.max(0, from); f < faces.length; f++) this.generatedFaces.add(faces[f]);
    return Math.max(0, faces.length - Math.max(0, from));
  }

  /**
   * The index of the first generated face in `this.geometry` — and so in any
   * PLY written from it — or −1 when the mesh has none.
   *
   * `toBufferGeometry` emits scan faces first and generated ones last, so the
   * generated faces are a contiguous suffix by construction and this single
   * integer identifies them: **faces [start, end) are generated, everything
   * before is scan.** That is the whole handover to the correction export.
   *
   * The count is taken from the DS, so `this.geometry` must be in sync with it
   * — call `updateGeometryFromHalfedges()` first if anything has changed.
   */
  public getGeneratedFacesFrom(): number {
    if (this.generatedFaces.size === 0) return -1;
    let scanFaces = 0;
    let generated = 0;
    for (const face of this.halfEdges.faces) {
      if (face.getVertices().length !== 3) continue;
      if (this.generatedFaces.has(face)) generated++;
      else scanFaces++;
    }
    return generated > 0 ? scanFaces : -1;
  }

  /** Faces `toBufferGeometry` would emit — non-triangles are dropped there. */
  private countTriangularFaces(): number {
    let count = 0;
    for (const face of this.halfEdges.faces) {
      if (face.getVertices().length === 3) count++;
    }
    return count;
  }

  /**
   * Converts the halfedge mesh back to a Three.js BufferGeometry.
   * This method creates a new BufferGeometry with updated positions, colors, and indices
   * based on the current state of the halfedge data structure.
   *
   * @returns A new BufferGeometry representing the current halfedge mesh
   */
  public toBufferGeometry(): BufferGeometry {
    const geometry = new BufferGeometry();

    // Create vertex index mapping
    const vertexToIndex = new Map<Vertex, number>();
    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    // Add all vertices to the geometry
    let vertexIndex = 0;
    for (const vertex of this.halfEdges.vertices) {
      vertexToIndex.set(vertex, vertexIndex);

      // Add position
      positions.push(vertex.position.x, vertex.position.y, vertex.position.z);

      // Add color (use vertex color if available, otherwise white)
      if (vertex.color) {
        colors.push(vertex.color.x, vertex.color.y, vertex.color.z);
      } else {
        colors.push(1.0, 1.0, 1.0); // Default white
      }

      vertexIndex++;
    }

    // Add all faces to the geometry. Generated faces go LAST, in a second
    // pass, so that they form a contiguous suffix the export can delete by a
    // single index — the DS pool order cannot be trusted for that, it
    // swap-pops on every removal.
    const deferred: number[] = [];
    const trackGenerated = this.generatedFaces.size > 0;
    for (const face of this.halfEdges.faces) {
      const vertices = face.getVertices();
      if (vertices.length === 3) {
        // Triangle face
        const a = vertexToIndex.get(vertices[0])!;
        const b = vertexToIndex.get(vertices[1])!;
        const c = vertexToIndex.get(vertices[2])!;
        if (trackGenerated && this.generatedFaces.has(face)) {
          deferred.push(a, b, c);
        } else {
          indices.push(a, b, c);
        }
      }
    }
    for (let i = 0; i < deferred.length; i++) indices.push(deferred[i]);

    // Set attributes
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);

    // Compute normals and other properties
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    return geometry;
  }

  /**
   * Updates the current mesh geometry with the halfedge mesh data.
   * This method replaces the current geometry with a new one generated from the halfedge structure.
   * It also updates the bounds tree and other computed properties.
   */
  public updateGeometryFromHalfedges(calculateBorderVertices = true): void { //TODO for now it can only be used together with setNewGeometry
    // Generate new geometry from halfedges
    const newGeometry = this.toBufferGeometry();

    // Dispose of the old geometry to free memory
    this.geometry.dispose();

    // Set the new geometry
    this.geometry = newGeometry;

    // Recompute bounds tree and other properties
    this.geometry.computeBoundsTree();
    this.geometry.computeVertexNormals();
    this.geometry.computeBoundingBox();

    if (calculateBorderVertices) {
      this.calculateBorderVertices();
    }
  }

  public setBorderVertices(vertices: Vertex[]) {
    this.borderVertices = vertices;
  }

  /**
   * Classical advancing‑front hole‑filling algorithm.
   * Creates 1 / 2 / 3 triangles per step based on the interior
   * angle (< 75°, 75–135°, > 135°). Works directly on the current
   * boundary loop returned by `extractOrderedBoundaryLoop()`.
   *
   * @param threshold – distance for snapping new vertices to an
   *                    existing boundary vertex (default 1 mm).
   * @return Set of vertex Ids that were added to the mesh.
   */
  public async advancingFront(mergeThreshold: number, upper: boolean): Promise<Set<number>> {
    return await new AdvancingFrontFiller(this, mergeThreshold, 12000, upper).fillHoles();
  }

  /**
   * Advancing-front variant that:
   *  - Limits the number of faces added to `maxFaces`.
   *  - At each step, considers only the `topN` highest-Y border vertices when choosing the next face.
   *
   * @param maxFaces Maximum number of faces to add.
   * @param topN Number of highest-Y boundary vertices to consider per step.
   * @param mergeThreshold Vertex merge threshold (same meaning as in `advancingFront`).
   * @returns Object with patched vertex IDs and the number of faces added.
   */
  public async advancingFrontLimited(maxFaces: number, topN: number, mergeThreshold = 0.15): Promise<{ patchedIds: Set<number>; facesAdded: number }> {
    const filler = new AdvancingFrontFiller(this, mergeThreshold, 12000, /*lowest*/ false);
    return await filler.fillHolesLimited(maxFaces, topN);
  }

  /**
   * Advancing-front fill that sweeps from the highest (or lowest) boundary vertices.
   * @param maxFaces Maximum number of faces to insert before stopping.
   * @param mergeThreshold Vertex merge threshold passed to the filler.
   * @param direction Optional sweep direction (defaults to the Y axis).
   * @param lowestFirst When true, sweep starts from the lowest vertices instead of the highest.
   */
  public async advancingFrontTopDown(
    maxFaces: number,
    mergeThreshold = 0.15,
    direction?: Vector3,
    lowestFirst = false,
  ): Promise<{ patchedIds: Set<number>; facesAdded: number }> {
    const filler = new AdvancingFrontFiller(this, mergeThreshold, 12000, /*lowest*/ false);
    return await filler.fillHolesTopDown(maxFaces, direction, lowestFirst);
  }

  /**
   * Fills a specific closed loop of boundary vertices using advancing-front algorithm.
   * Unlike the other advancing-front methods, this only processes vertices from the
   * provided loop, allowing multiple separate boundaries to coexist without being filled.
   *
   * @param boundaryLoop Array of vertices forming the closed loop to fill
   * @param mergeThreshold Vertex merge threshold
   * @returns Set of vertex IDs that were patched
   */
  public async advancingFrontSpecificLoop(
    boundaryLoop: Vertex[],
    mergeThreshold = 0.15,
  ): Promise<Set<number>> {
    const filler = new AdvancingFrontFiller(this, mergeThreshold, 1000, /*lowest*/ false);
    return await filler.fillHoles(boundaryLoop);
  }

  /**
   * Processes all border vertices and creates a sorted array sorted by interior angles.
   * For each border vertex, gets its boundary information (halfedges and adjacent vertices)
   * and calculates the interior angle. Returns a sorted array with smallest angles first.
   *
   * @param vertexToBoundaryInfo Map to populate with vertex to boundary info mappings
   * @param nHeighest Number of highest vertices to consider
   * @param lowest If true, sort from lowest to highest instead of highest to lowest
   * @param excludeVertices Optional set of vertices to skip (for filling specific loops)
   * @param allowedHalfedges Optional set of halfedges to filter by (for filling specific loops)
   * @returns SortedArray containing adjacenceBoundaries objects sorted by angle (smallest first)
   */
  public sortBorderVerticesByAngle(
    vertexToBoundaryInfo: Map<Vertex, adjacenceBoundaries>,
    nHeighest: number,
    lowest = false,
    excludeVertices?: Set<Vertex>,
    allowedHalfedges?: Set<Halfedge>,
  ): { sortedArray: SortedArray<adjacenceBoundaries>; lowestVertex: Vertex } {
    const sortedArray = new SortedArray<adjacenceBoundaries>((item) => item.angle);
    let lowestVertex = this.borderVertices[0];

    // sort vertices by height
    let sortedByHeight: Vertex[];
    if (!lowest) {
      sortedByHeight = this.borderVertices.sort((a, b) => b.position.y - a.position.y);
    } else {
      sortedByHeight = this.borderVertices.sort((a, b) => a.position.y - b.position.y);
    }

    // Process each border vertex
    for (let i = 0; i < sortedByHeight.length && i < nHeighest; i++) {
      const vertex = sortedByHeight[i];
      
      // Skip vertices not in our target loop
      if (excludeVertices && excludeVertices.has(vertex)) {
        continue;
      }
      
      const boundaryInfo = vertex.getBoundaryHalfedgesAndVerticesInfo();
      if (
        boundaryInfo !== null &&
        allowedHalfedges &&
        (!allowedHalfedges.has(boundaryInfo.incomingHalfedge) || !allowedHalfedges.has(boundaryInfo.outgoingHalfedge))
      ) {
        continue;
      }

      if (boundaryInfo !== null) {
        if (vertex.position.y < lowestVertex.position.y) {
          lowestVertex = vertex;
        }
        // Insert into sorted array (automatically maintains sorted order by angle)
        sortedArray.insert(boundaryInfo);
        vertexToBoundaryInfo.set(vertex, boundaryInfo);
      }
    }

    return { sortedArray, lowestVertex };
  }

  public highlightVertices() {
    // Remove any existing highlights first
    this.removeVertexHighlight();

    // Highlight all vertices in the halfedge mesh
    for (const vertex of this.halfEdges.vertices) {
      this.highlightVertexWithHalfedges(vertex);
    }
  }

  public highlightVertexWithHalfedges(vertex: Vertex, color: Color = new Color(0xff0000)): void {
    // 1. Highlight the selected vertex with a sphere
    const sphereGeometry = new SphereGeometry(0.005, 16, 16);
    const sphereMaterial = new MeshBasicMaterial({ color: color });
    const vertexSphere = new Mesh(sphereGeometry, sphereMaterial);
    vertexSphere.position.copy(vertex.position);
    vertexSphere.name = 'vertex-highlight-sphere';
    this.rootState.scene.add(vertexSphere);
    this.highlightObjects.push(vertexSphere);

    // 2. Create arrows for all halfedges originating from this vertex
    let edgeIndex = 0;
    for (const halfedge of vertex.loopCW()) {
      if (halfedge.vertex === vertex) {
        let boundaryIn = false;
        let boundaryOut = false;
        if (halfedge.twin.isBoundary()) {
          // Skip boundary halfedges for now
          boundaryIn = true;
        }
        if (halfedge.isBoundary()) {
          // Skip boundary halfedges for now
          boundaryOut = true;
        }
        // This is an outgoing halfedge from our vertex
        const targetVertex = halfedge.twin.vertex;

        // Create arrow from source to target
        const direction = new Vector3().subVectors(targetVertex.position, vertex.position);
        const length = direction.length();
        direction.normalize();

        // Create arrow using ArrowHelper
        let arrowColor = new Color(0x000000); // black color
        if (boundaryIn) {
          arrowColor = new Color(0xff0000); // red for boundary edges
        }
        const arrow = new ArrowHelper(direction, vertex.position, length * 0.8, arrowColor.getHex(), length * 0.1, length * 0.05);
        arrow.name = `halfedge-arrow-${edgeIndex}`;
        this.rootState.scene.add(arrow);
        this.highlightObjects.push(arrow);

        arrowColor = new Color(0x000000);
        if (boundaryOut) {
          arrowColor = new Color(0xff0000);
        }
        const arrowOut = new ArrowHelper(direction.negate(), targetVertex.position, length * 0.8, arrowColor.getHex(), length * 0.1, length * 0.05);
        arrowOut.name = `halfedge-arrow-out-${edgeIndex}`;
        this.rootState.scene.add(arrowOut);
        this.highlightObjects.push(arrowOut);

        edgeIndex++;
      }
    }

    // 4. Highlight adjacent vertices with smaller spheres
    let adjIndex = 0;
    for (const adjVertex of vertex.adjacentVertices()) {
      const adjSphereGeometry = new SphereGeometry(0.005, 12, 12);
      const adjSphereMaterial = new MeshBasicMaterial({
        color: new Color().setHSL((adjIndex * 0.2) % 1, 0.6, 0.8),
        transparent: true,
        opacity: 0.7,
      });
      const adjSphere = new Mesh(adjSphereGeometry, adjSphereMaterial);
      adjSphere.position.copy(adjVertex.position);
      adjSphere.name = `adjacent-vertex-${adjIndex}`;
      this.rootState.scene.add(adjSphere);
      this.highlightObjects.push(adjSphere);
      adjIndex++;
    }
  }

  /**
   * Removes all vertex highlight objects from the scene.
   */
  public removeVertexHighlight(): void {
    // Remove 3D objects
    for (const obj of this.highlightObjects) {
      this.rootState.scene.remove(obj);
      // Dispose of geometries and materials to free memory
      if (obj instanceof Mesh) {
        obj.geometry.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach((mat) => mat.dispose());
        } else {
          obj.material.dispose();
        }
      }
    }
    delete (this as any)._highlightObjects;
  }

  public async smoothBorderVertices(options?: SmoothingOptions): Promise<void> {
    await new SmoothBorder(this).smoothBorderVertices(options ?? {});
  }

  /**
   * Push selected vertex/vertices and their rings toward a target position
   * with configurable falloff (exponential/linear/gaussian) and smoothing.
   */
  public async pushVerticesTowardsPosition(
    vertices: Vertex[] | Vertex,
    target: Vector3,
    options: PushTowardsPositionOptions = {}
  ): Promise<void> {
    await new PushVerticesTowardsPosition(this).apply(vertices, target, options);
  }

  /**
   * Creates an ordered array of border vertices by following the boundary loop
   * using halfedge next connections
   */
  public getConnectedBorderVertices(): Vertex[] {
    const borderVertices = this.getBorderVertices();

    if (borderVertices.length === 0) {
      return [];
    }

    const connectedVertices: Vertex[] = [];
    const visited = new Set<Vertex>();

    // Start with the first border vertex
    let currentVertex = borderVertices[0];
    const startVertex = currentVertex;

    do {
      // Add current vertex to the result
      connectedVertices.push(currentVertex);
      visited.add(currentVertex);

      // Find the next boundary vertex by following the boundary halfedge
      let nextVertex: Vertex | null = null;

      // Look for a boundary halfedge starting from current vertex
      for (const halfedge of currentVertex.boundaryHalfedgesOutLoop()) {
        if (halfedge.face === null) {
          // This is a boundary halfedge
          // Follow this halfedge to get the next vertex
          nextVertex = halfedge.twin.vertex;
          break;
        }
      }

      // If we couldn't find a next vertex through outgoing halfedges,
      // try using the boundary info method
      if (!nextVertex) {
        const boundaryInfo = currentVertex.getBoundaryHalfedgesAndVerticesInfo();
        if (boundaryInfo) {
          nextVertex = boundaryInfo.nextVertex;
        }
      }

      // Safety checks
      if (!nextVertex || nextVertex === startVertex || visited.has(nextVertex)) {
        break;
      }

      // Make sure the next vertex is actually a border vertex
      if (!borderVertices.includes(nextVertex)) {
        break;
      }

      currentVertex = nextVertex;

      // Safety check to prevent infinite loops
      if (connectedVertices.length >= borderVertices.length) {
        break;
      }
    } while (currentVertex !== startVertex);

    return connectedVertices;
  }

  /**
   * Next vertex along the boundary loop. Prefer the vendor boundary-pair helper,
   * then fall back to direct boundary adjacency for edited/non-manifold borders.
   */
  public getNextBorderLoopVertex(current: Vertex, previous?: Vertex | null): Vertex | null {
    const borderIds = new Set(this.borderVertices.map((v) => v.id));
    const boundaryInfo = current.getBoundaryHalfedgesAndVerticesInfo();
    if (
      boundaryInfo &&
      borderIds.has(boundaryInfo.nextVertex.id) &&
      boundaryInfo.nextVertex.id !== previous?.id
    ) {
      return boundaryInfo.nextVertex;
    }

    const neighbors = current
      .getAdjacenceBoundaryVertices()
      .filter((neighbor, index, all) =>
        borderIds.has(neighbor.id) &&
        neighbor.id !== previous?.id &&
        all.findIndex((candidate) => candidate.id === neighbor.id) === index,
      );

    return neighbors[0] ?? null;
  }

  /** Ordered boundary components, built from boundary-vertex adjacency. */
  public getBorderLoops(): Vertex[][] {
    this.calculateBorderVertices();
    const border = this.borderVertices;
    if (border.length === 0) return [];

    const borderIds = new Set(border.map((v) => v.id));
    const visitedIds = new Set<number>();
    const loops: Vertex[][] = [];

    const neighborsOf = (vertex: Vertex) =>
      vertex
        .getAdjacenceBoundaryVertices()
        .filter((neighbor, index, all) =>
          borderIds.has(neighbor.id) &&
          all.findIndex((candidate) => candidate.id === neighbor.id) === index,
        );

    for (const start of border) {
      if (visitedIds.has(start.id)) continue;

      const loop: Vertex[] = [];
      let previous: Vertex | null = null;
      let current: Vertex | null = start;

      while (current && !visitedIds.has(current.id)) {
        loop.push(current);
        visitedIds.add(current.id);

        const neighbors = neighborsOf(current);
        const next =
          neighbors.find((neighbor) => neighbor.id !== previous?.id && !visitedIds.has(neighbor.id)) ??
          neighbors.find((neighbor) => neighbor.id === start.id && loop.length > 2) ??
          null;

        if (!next || next.id === start.id) break;
        previous = current;
        current = next;
      }

      if (loop.length > 0) loops.push(loop);
    }

    return loops;
  }

  /** Yields all border vertices once, grouped internally by boundary component. */
  public *walkBorderVertices(): Generator<Vertex, void, unknown> {
    for (const loop of this.getBorderLoops()) {
      for (const vertex of loop) {
        yield vertex;
      }
    }
  }

  public getCentroid(): Vector3 {
    const positions = this.halfEdges.vertices.map((v) => v.position);
    const sum = positions.reduce((acc, pos) => acc.add(pos), new Vector3());
    return sum.divideScalar(positions.length);
  }

  public getLowestBorderVertex(): Vertex | null {
    if (this.borderVertices.length === 0) {
      return null;
    }

    let lowestVertex = this.borderVertices[0];
    for (const vertex of this.borderVertices) {
      if (vertex.position.y < lowestVertex.position.y) {
        lowestVertex = vertex;
      }
    }
    return lowestVertex;
  }

  public getHighestBorderVertex(direction?: Vector3): Vertex | null {
    if (this.borderVertices.length === 0) {
      return null;
    }
    let evalDir: Vector3 | undefined;
    if (direction && direction.lengthSq() > 1e-12) {
      evalDir = direction.clone().normalize();
    }
    let highestVertex = this.borderVertices[0];
    let highestScore = evalDir ? highestVertex.position.dot(evalDir) : highestVertex.position.y;
    for (const vertex of this.borderVertices) {
      const score = evalDir ? vertex.position.dot(evalDir) : vertex.position.y;
      if (score > highestScore) {
        highestVertex = vertex;
        highestScore = score;
      }
    }
    return highestVertex;
  }

  public fillSides(n: number, top: boolean, direction?: Vector3): void {
    fillSides(this, n, top, direction ? { direction } : undefined);
    this.calculateBorderVertices();
  }

  /**
   * Flattens the current boundary vertices to a target Y-plane and collapses overlapping boundary edges.
   * @param mode 'meshMin' | 'borderMin' | number (absolute Y)
   */
  public flattenBorderToY(mode: FlattenYMode, opts: FlattenBorderOptions = {}): void {
    flattenBorderToY(this, mode, opts);
  }

  /** Scales provided vertices (or the current border if vertices undefined/empty) around a center. */
  public scaleVerticesBulk(vertices: Vertex[] | undefined, opts: ScaleVerticesOptions): void {
    const verts = vertices && vertices.length ? vertices : this.getBorderVertices();
    scaleVertices(this, verts, opts);
  }


  public removeVertex(vertex: Vertex): Array<Vertex> {
    const adjacentVertices = [...vertex.adjacentVertices()];
    this.halfEdges.removeVertex(vertex);
    return adjacentVertices;
  }

  public batchRemoveVertices(vertices: Vertex[]): Array<Vertex> {
    const adjacentVertices: Set<Vertex> = new Set();
    for (const vertex of vertices) {
      const adjacents = this.removeVertex(vertex);
      adjacents.forEach((adj) => adjacentVertices.add(adj));
    }
    vertices.forEach((v) => adjacentVertices.delete(v));
    return Array.from(adjacentVertices);
  }

  public getClosestVertexToIntersection(hit: Intersection): Vertex | null {
    const [ia, ib, ic] = this.indicesFromHit(hit);

    const vertexA = this.halfEdges.renderIdToVertex.get(ia);
    const vertexB = this.halfEdges.renderIdToVertex.get(ib);
    const vertexC = this.halfEdges.renderIdToVertex.get(ic);

    if (!vertexA || !vertexB || !vertexC) return null;

    // get closest vertex
    const pLocal = hit.point.clone();
    this.worldToLocal(pLocal);    
    let closestVertex: Vertex | null = null;
    let closestDistance = Infinity;

    for (const vertex of [vertexA, vertexB, vertexC]) {
      const distance = vertex.position.distanceToSquared(pLocal);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestVertex = vertex;
      }
    }

    return closestVertex;
  }

  public indicesFromHit(hit: Intersection) {
    const idx = this.geometry.getIndex();
    const base = hit.faceIndex! * 3;
    const ia = idx ? idx.getX(base + 0) : base + 0;
    const ib = idx ? idx.getX(base + 1) : base + 1;
    const ic = idx ? idx.getX(base + 2) : base + 2;
    return [ia, ib, ic];
  }

  /** World-space centroid of a triangular face on this mesh. */
  public faceCentroidWorld(face: Face, out: Vector3): void {
    faceCentroidWorld(this, face, out);
  }

  /** Shortest distance from a world-space point to another mesh surface (BVH). */
  public getWorldDistanceToMeshSurface(targetMesh: HalfEdgeMesh, worldPoint: Vector3): number | null {
    return worldDistanceToMeshSurface(worldPoint, targetMesh);
  }

  /** Faces on this mesh within `maxDistWorld` of `targetMesh` surface. */
  public collectFacesNearMeshSurface(
    targetMesh: HalfEdgeMesh,
    maxDistWorld: number,
    filter?: FaceProximityFilter,
  ): FaceProximityHit[] {
    return collectFacesNearMeshSurface(this, targetMesh, maxDistWorld, filter);
  }

  /** Render-only geometry for a subset of faces (local space). */
  public buildGeometryFromFaceSet(includedFaces: Set<Face>): BufferGeometry {
    return buildGeometryFromFaceSet(this, includedFaces);
  }

  /** Line geometry tracing only the outline (perimeter) of a face patch (local space). */
  public buildFaceSetOutlineGeometry(includedFaces: Set<Face>, liftLocal = 0): BufferGeometry {
    return buildFaceSetOutlineGeometry(this, includedFaces, liftLocal);
  }

  /** Proximity band width from mean edge length (segmentation / correction default). */
  public defaultProximityThresholdWorld(factor = 0.55): number {
    return computeDefaultProximityThresholdWorld(this, factor);
  }

  /**
   * Removes faces by rebuilding geometry from the kept face set.
   * Invalidates existing `Vertex` / `Face` references on this mesh.
   */
  public removeFaceSet(facesToRemove: Iterable<Face>): RemoveFaceSetResult {
    return removeFaceSetOp(this, facesToRemove);
  }
}
